import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { loginUser, resetUserPassword } from '../services/login_service'
import { AUTH_TOKEN_KEY, setUnauthorizedHandler } from '../services/api_config'

const AUTH_STORAGE_KEY = 'buck-press-auth-user'
const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000
const AuthContext = createContext(null)

function getUserPermissions(user) {
  const rolePermissions =
    user?.role_permissions ?? user?.rolePermissions ?? user?.permissions ?? []

  if (!Array.isArray(rolePermissions)) return []

  return rolePermissions
    .map((permission) =>
      typeof permission === 'string'
        ? permission
        : (permission?.permission ??
          permission?.permission_name ??
          permission?.name ??
          permission?.action),
    )
    .filter(Boolean)
}

function getStoredUser() {
  try {
    const storedUser = window.localStorage.getItem(AUTH_STORAGE_KEY)
    return storedUser ? JSON.parse(storedUser) : null
  } catch {
    window.localStorage.removeItem(AUTH_STORAGE_KEY)
    return null
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser)
  const timeoutRef = useRef(null)
  const permissions = getUserPermissions(user)

  const clearSession = () => {
    window.localStorage.removeItem(AUTH_STORAGE_KEY)
    window.localStorage.removeItem(AUTH_TOKEN_KEY)
    setUser(null)
  }

  useEffect(() => setUnauthorizedHandler(clearSession), [])

  const resetInactivityTimer = () => {
    if (!user) return

    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = window.setTimeout(() => {
      clearSession()
    }, INACTIVITY_TIMEOUT_MS)
  }

  useEffect(() => {
    if (!user) return undefined

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ]

    resetInactivityTimer()

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetInactivityTimer, {
        passive: true,
      })
    })

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
      }

      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetInactivityTimer)
      })
    }
  }, [user])

  const value = useMemo(
    () => ({
      user,
      permissions,
      isAuthenticated: Boolean(user),
      requiresPasswordReset: Boolean(user?.requires_password_reset),
      hasPermission: (permission) => permissions.includes(permission),
      hasAnyPermission: (requiredPermissions = []) =>
        !requiredPermissions.length ||
        requiredPermissions.some((permission) =>
          permissions.includes(permission),
        ),
      login: async (email, password) => {
        if (!email || !password)
          throw new Error('Email and password are required.')
        const { user: authenticatedUser, token } = await loginUser(
          email,
          password,
        )
        window.localStorage.setItem(
          AUTH_STORAGE_KEY,
          JSON.stringify(authenticatedUser),
        )
        if (token) window.localStorage.setItem(AUTH_TOKEN_KEY, token)
        setUser(authenticatedUser)
        return authenticatedUser
      },
      resetPassword: async (currentPassword, newPassword, confirmPassword) => {
        if (!user) throw new Error('Sign in before resetting your password.')
        const userId = user.userid || user.user_id || user.id
        const updatedUser = await resetUserPassword(
          userId,
          currentPassword,
          newPassword,
          confirmPassword,
        )
        window.localStorage.setItem(
          AUTH_STORAGE_KEY,
          JSON.stringify(updatedUser),
        )
        setUser(updatedUser)
        return updatedUser
      },
      logout: () => {
        clearSession()
      },
    }),
    [permissions, user],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
